import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, notification } from 'antd';
import { UserAddOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { useSelection } from '../../SelectionContext';

interface QuickClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (client: any) => void;
  currentWarehouseId?: number;
}

interface QuickClientForm {
  name: string;
  email?: string;
  phone?: string;
}

const QuickClientModal: React.FC<QuickClientModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  currentWarehouseId
}) => {
  const { user, hasPermission } = useAuth();
  const { selectedCompany, selectedWarehouse } = useSelection();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const canCreateClient =
    user?.is_superadmin === true ||
    Number(user?.is_superadmin) === 1 ||
    hasPermission("Gestion Commerciale.Entites.Clients.create");

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
    }
  }, [isOpen, form]);

  const handleSubmit = async (values: QuickClientForm) => {
    if (!canCreateClient) {
      notification.error({
        message: 'Permission insuffisante',
        description: 'La permission de création des clients est requise.',
        duration: 3,
      });
      return;
    }

    if (!selectedCompany) {
      notification.error({
        message: 'Erreur',
        description: 'Veuillez sélectionner une entreprise dans l\'en-tête',
        duration: 3,
      });
      return;
    }

    if (!selectedWarehouse) {
      notification.error({
        message: 'Erreur',
        description: 'Veuillez sélectionner un établissement dans l\'en-tête',
        duration: 3,
      });
      return;
    }

    setLoading(true);
    
    try {
      const authToken =
        localStorage.getItem("authToken") || localStorage.getItem("token");
      const clientData = new FormData();
      clientData.append('name', values.name);
      if (values.email && values.email.trim()) {
        clientData.append('email', values.email.trim());
      }
      if (values.phone && values.phone.trim()) {
        clientData.append('phone', values.phone.trim());
      }
      clientData.append('user_type', 'customers');
      clientData.append('status', 'enabled');
      
      // CORRECTION: Utiliser la même logique que Gestion.entitie_client.jsx
      // Utiliser selectedCompany et selectedWarehouse du contexte
      const warehouseId = selectedWarehouse?.id ?? selectedWarehouse;
      const companyId = selectedCompany?.id ?? selectedCompany;
      
      
      // Ajouter warehouse_id ET details_warehouse_id (comme la création normale)
      clientData.append('warehouse_id', Number(warehouseId).toString());
      clientData.append('details_warehouse_id', Number(warehouseId).toString());
      
      // Ajouter company_id (comme la création normale)
      clientData.append('company_id', Number(companyId).toString());
      
      // Ajouter les champs par défaut requis par le backend (comme routes/users.js)
      clientData.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
      clientData.append('date_format', 'd-m-Y');
      clientData.append('date_picker_format', 'dd-mm-yyyy');
      clientData.append('time_format', 'h:i a');

      const response = await fetch('/api/users', {
        method: 'POST',
        headers: authToken
          ? { Authorization: `Bearer ${authToken}` }
          : {},
        body: clientData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la création du client');
      }

      const result = await response.json();
      const createdUserId = result?.id;
      
      // Récupérer les détails complets du client créé avec filtrage par warehouse
      // Réutiliser les mêmes IDs que pour la création
      
      const queryParams = new URLSearchParams({
        companyId: Number(companyId).toString(),
        warehouseId: Number(warehouseId).toString()
      });
      
      const clientResponse = await fetch(`/api/users/customers?${queryParams.toString()}`, {
        headers: authToken
          ? { Authorization: `Bearer ${authToken}` }
          : {},
      });

      if (clientResponse.ok) {
        const clients = await clientResponse.json();
        const newClient = createdUserId
          ? clients.find((c: any) => c.id === createdUserId)
          : clients.find((c: any) => c.name === values.name);
        
        if (newClient) {
          onSuccess(newClient);
          notification.success({
            message: 'Succès',
            description: `Client "${values.name}" créé avec succès`,
            duration: 3,
          });
          onClose();
        }
      }
    } catch (error) {
      console.error('Erreur lors de la création du client:', error);
      notification.error({
        message: 'Erreur',
        description: error instanceof Error ? error.message : 'Une erreur est survenue',
        duration: 4,
      });
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (_: any, value?: string) => {
    if (!value || !value.trim()) {
      return Promise.resolve();
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value.trim())) {
      return Promise.reject(new Error('Format d\'email invalide'));
    }
    return Promise.resolve();
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <UserAddOutlined className="text-blue-600" />
          <span>Nouveau Client Rapide</span>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={480}
      destroyOnClose
      centered
    >
      <div className="pt-4">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          disabled={loading || !canCreateClient}
        >
          <Form.Item
            label="Nom complet"
            name="name"
            rules={[
              { required: true, message: 'Le nom est requis' },
              { min: 2, message: 'Le nom doit contenir au moins 2 caractères' },
              { max: 100, message: 'Le nom ne peut pas dépasser 100 caractères' }
            ]}
          >
            <Input
              placeholder="Entrez le nom du client"
              size="large"
              autoFocus
            />
          </Form.Item>

          <Form.Item
            label="Email (optionnel)"
            name="email"
            rules={[{ validator: validateEmail }]}
          >
            <Input
              type="email"
              placeholder="client@example.com"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Téléphone (optionnel)"
            name="phone"
            rules={[
              { max: 20, message: 'Le numéro ne peut pas dépasser 20 caractères' }
            ]}
          >
            <Input
              placeholder="+229 XX XX XX XX"
              size="large"
            />
          </Form.Item>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              onClick={onClose}
              disabled={loading}
              size="large"
            >
              Annuler
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={!canCreateClient}
              size="large"
              icon={<UserAddOutlined />}
            >
              Créer Client
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default QuickClientModal;
